import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, DollarSign, MapPin, Search, RefreshCcw, 
  FileText, TrendingDown, Check, Save, 
  Trash2, Eye, EyeOff, AlertCircle, X, Calendar, Activity, Truck,
  PieChart, Filter, ArrowDownCircle, ArrowUpCircle, Edit3
} from 'lucide-react';
import toast from 'react-hot-toast';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';
import { buildLocalOperationTimestamp } from '../utils/localDateTime';

// Condición de pago del gasto. 'cuenta_corriente' no mueve fondos: genera deuda
// con el proveedor (proveedor_movimientos tipo 'Gasto') que se cancela desde
// Tesorería > Pagos.
const METODO_CTA_CTE = 'Cuenta Corriente';
const isGastoCtaCte = (g) => g?.condicion_pago === 'cuenta_corriente';

/**
 * Gestión de Gastos Operativos Enterprise
 * REVISION 2: UI Estandar + Categoría Fijo/Variable + Edición en Click + Confirmación Borrado Texto
 */
function Gastos() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Estado para anulación
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [processingDeleteId, setProcessingDeleteId] = useState(null);
  const [mostrarEliminados, setMostrarEliminados] = useState(false);

  const [gastos, setGastos] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  
  // COMENTADO: Funcionalidad anterior de expandir detalles
  // const [expandedId, setExpandedId] = useState(null);

  // Filtros
  const [q, setQ] = useState('');
  const [filterSitio, setFilterSitio] = useState('all');
  const [filterCategoria, setFilterCategoria] = useState('all'); 

  const [fechaDesde, setFechaDesde] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [fechaHasta, setFechaHasta] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Formulario Nuevo/Editar Gasto
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Estado para saber si editamos
  const [form, setForm] = useState({
    id: null, // ID para edición
    concepto: '',
    monto: '',
    fecha: new Date().toISOString().split('T')[0],
    sitio_id: '',
    observaciones: '',
    metodo_pago: 'Efectivo',
    banco_id: '',
    proveedor_id: '',
    categoria: 'Variable'
  });

  useEffect(() => {
    if (profile?.organization_id) {
      fetchAuxData();
      loadGastos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, fechaDesde, fechaHasta, mostrarEliminados]);

  useEffect(() => {
    const siteId = form.sitio_id || (filterSitio !== 'all' ? filterSitio : '');
    if (siteId) persistSelectedSiteId(siteId);
  }, [form.sitio_id, filterSitio]);

  const fetchAuxData = async () => {
    try {
        const [s, b, p] = await Promise.all([
            supabase.from('sitios').select('id, nombre').eq('organization_id', profile.organization_id).eq('is_active', true),
            supabase.from('banks').select('id, name').eq('organization_id', profile.organization_id).eq('is_active', true),
            supabase.from('proveedores').select('id, razon_social, is_active').eq('organization_id', profile.organization_id).order('razon_social')
        ]);
        setProveedores((p.data || []).filter((prov) => prov.is_active !== false));
        const availableSites = s.data || [];
        const preferredSiteId = resolvePreferredSiteId(availableSites, {
          currentSiteId: form.sitio_id || (filterSitio !== 'all' ? filterSitio : ''),
        });
        setSitios(availableSites);
        setForm((prev) => ({
          ...prev,
          sitio_id: resolvePreferredSiteId(availableSites, {
            currentSiteId: prev.sitio_id,
            fallbackSiteId: preferredSiteId,
          }),
        }));
        setFilterSitio((prev) => {
          const nextSiteId = resolvePreferredSiteId(availableSites, {
            currentSiteId: prev === 'all' ? '' : prev,
            fallbackSiteId: preferredSiteId,
          });
          return nextSiteId || 'all';
        });
        setBancos(b.data || []);
    } catch(e) { console.error(e); }
  };

  const loadGastos = async () => {
    setLoading(true);
    try {
      const orgId = profile.organization_id;

      // ============================================================
      // BORDES DE FECHA SEGUROS
      // ============================================================
      // Si la columna es DATE, la comparación funciona con string YYYY-MM-DD.
      // Si la columna es TIMESTAMP (gastos, movimientos_caja pueden serlo según
      // schema), hace falta extender el "hasta" al final del día, sino el
      // .lte('fecha','2024-01-15') excluye todo registro de ese día con hora > 0.
      // Por eso usamos los dos formatos: el plano para queries DATE-safe, y el
      // extendido para queries TIMESTAMP-safe.
      const desdeInicio = `${fechaDesde}T00:00:00`;
      const hastaFin = `${fechaHasta}T23:59:59.999`;

      // Helper para comparar "mismo día calendario" de dos fechas en string
      // YYYY-MM-DD (sin caer en problemas de timezone ni perder el año).
      const sameCalendarDay = (a, b) => {
        if (!a || !b) return false;
        const aIso = String(a).slice(0, 10);
        const bIso = String(b).slice(0, 10);
        return aIso === bIso;
      };

      // 1. GASTOS MANUALES
      let queryGastos = supabase
        .from('gastos')
        .select('*, sitios(nombre), proveedores(razon_social)')
        .eq('organization_id', orgId)
        .gte('fecha', desdeInicio)
        .lte('fecha', hastaFin)
        .order('fecha', { ascending: false })
        // Subimos el techo por defecto de 1000 para no perder historial largo.
        .limit(5000);

      if (!mostrarEliminados) {
          queryGastos = queryGastos.eq('is_active', true);
      }

      // 2. COSTOS DE ENVÍO Y COMISIONES (Automáticos)
      let queryFinanciero = supabase
        .from('movimientos_financieros')
        .select('id, monto, descripcion, fecha_operacion, sitio_id, categoria, sitios(nombre)')
        .eq('organization_id', orgId)
        .eq('tipo', 'Egreso')
        .eq('is_active', true)
        .or('categoria.in.("Fletes y Envíos","Impuestos (Comisiones)","Gasto Operativo"),descripcion.ilike.%Envío%,descripcion.ilike.%Flete%')
        .gte('fecha_operacion', desdeInicio)
        .lte('fecha_operacion', hastaFin)
        .limit(5000);

      // 3. COSTOS DE ENVÍO EN CAJA
      // ✅ FIX multi-sucursal: traemos sitio_id (vía turno) para que el gasto automático tenga sucursal real
      let queryCaja = supabase
        .from('movimientos_caja')
        .select('id, monto, descripcion, fecha, categoria, sitio_id, sitios(nombre), turno_id, turnos_caja(sitio_id, sitios(nombre))')
        .eq('organization_id', orgId)
        .eq('tipo', 'Gasto')
        .or('categoria.eq.Fletes y Envíos,descripcion.ilike.%Envío%,descripcion.ilike.%Flete%')
        .gte('fecha', desdeInicio)
        .lte('fecha', hastaFin)
        .limit(5000);

      const [resGastos, resFinanciero, resCaja] = await Promise.all([queryGastos, queryFinanciero, queryCaja]);

      if (resGastos.error) throw resGastos.error;
      if (resFinanciero.error) console.warn('Error parcial cargando movimientos financieros:', resFinanciero.error);
      if (resCaja.error) console.warn('Error parcial cargando movimientos de caja:', resCaja.error);

      const gastosManuales = resGastos.data || [];

      // ============================================================
      // DEDUPLICACIÓN GASTO MANUAL ↔ MOVIMIENTO AUTOMÁTICO
      // ============================================================
      // Antes comparaba día y mes pero NO el año, así que un movimiento de
      // 2023-01-15 se podía marcar como "duplicado" de un gasto manual de
      // 2024-01-15. Ahora comparamos el día calendario completo (YYYY-MM-DD).
      const gastosAutomaticosFin = (resFinanciero.data || [])
          .filter(m => {
              const mDesc = (m.descripcion || '').toUpperCase();
              return !gastosManuales.some(g => {
                  if (!sameCalendarDay(g.fecha, m.fecha_operacion)) return false;
                  const gConc = (g.concepto || '').toUpperCase();
                  return Math.abs(Number(g.monto || 0) - Number(m.monto || 0)) < 0.1
                      && (gConc.includes(mDesc) || mDesc.includes(gConc));
              });
          })
          .map(m => ({
              id: m.id,
              concepto: `${m.categoria}: ${m.descripcion}`,
              monto: m.monto,
              fecha: m.fecha_operacion.split('T')[0],
              sitio_id: m.sitio_id,
              sitios: m.sitios,
              observaciones: 'Movimiento automático generado desde Pedidos Web (Banco)',
              is_active: true,
              origen: 'automatico',
              categoria: 'Variable'
          }));

      const gastosAutomaticosCaja = (resCaja.data || [])
          .filter(m => {
              const mDesc = (m.descripcion || '').toUpperCase();
              return !gastosManuales.some(g => {
                  if (!sameCalendarDay(g.fecha, m.fecha)) return false;
                  const gConc = (g.concepto || '').toUpperCase();
                  return Math.abs(Number(g.monto || 0) - Number(m.monto || 0)) < 0.1
                      && (gConc.includes(mDesc) || mDesc.includes(gConc));
              });
          })
          .map(m => {
              // ✅ FIX multi-sucursal: priorizamos sitio_id directo, si no, lo derivamos del turno asociado
              const sitioIdResuelto = m.sitio_id || m.turnos_caja?.sitio_id || null;
              const sitiosResuelto = m.sitios || m.turnos_caja?.sitios || (sitioIdResuelto ? null : { nombre: 'Caja' });
              return {
                  id: m.id,
                  concepto: `${m.categoria}: ${m.descripcion}`,
                  monto: m.monto,
                  fecha: m.fecha.split('T')[0],
                  sitio_id: sitioIdResuelto,
                  sitios: sitiosResuelto,
                  observaciones: 'Movimiento automático generado desde Pedidos Web (Caja)',
                  is_active: true,
                  origen: 'automatico',
                  categoria: 'Variable'
              };
          });

      const combinados = [...gastosManuales, ...gastosAutomaticosFin, ...gastosAutomaticosCaja]
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

      setGastos(combinados);

    } catch (err) {
      console.error(err);
      toast.error('Error cargando gastos');
    } finally {
      setLoading(false);
    }
  };

  // --- NUEVO: Función para abrir modal en modo Edición ---
  const handleEditClick = (gasto) => {
    // Si es automático, no permitimos edición completa (o podríamos bloquear campos)
    if(gasto.origen === 'automatico') {
        toast('Los gastos automáticos no se pueden editar aquí', { icon: '🔒' });
        return;
    }
    
    // Si está anulado, no editar
    if(!gasto.is_active) return;

    setForm({
        id: gasto.id,
        concepto: gasto.concepto,
        monto: gasto.monto,
        fecha: gasto.fecha,
        sitio_id: gasto.sitio_id || '',
        observaciones: gasto.observaciones || '',
        metodo_pago: isGastoCtaCte(gasto) ? METODO_CTA_CTE : 'Efectivo', // Visual: el método no se edita
        banco_id: '',
        proveedor_id: gasto.proveedor_id || '',
        condicion_pago: gasto.condicion_pago || 'contado',
        monto_original: gasto.monto,
        categoria: gasto.categoria || 'Variable'
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleCreateClick = () => {
      const preferredSiteId = resolvePreferredSiteId(sitios, {
        currentSiteId: form.sitio_id || (filterSitio !== 'all' ? filterSitio : ''),
      });
      setForm({
        id: null,
        concepto: '',
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        sitio_id: preferredSiteId,
        observaciones: '',
        metodo_pago: 'Efectivo',
        banco_id: '',
        proveedor_id: '',
        categoria: 'Variable'
      });
      setIsEditing(false);
      setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.concepto || !form.monto) return toast.error("Datos incompletos");
    if (!isEditing && form.metodo_pago === 'Transferencia' && !form.banco_id) return toast.error("Seleccione un banco");
    if (!isEditing && form.metodo_pago === METODO_CTA_CTE && !form.proveedor_id) return toast.error("Seleccione el proveedor");
    // Un gasto en cuenta corriente ya impactó el saldo del proveedor: cambiar el
    // monto lo desfasaría. Se corrige anulando y volviendo a cargar.
    if (isEditing && form.condicion_pago === 'cuenta_corriente' && Number(form.monto) !== Number(form.monto_original)) {
        return toast.error("El monto de un gasto en cuenta corriente no se edita: anulalo y cargalo de nuevo");
    }

    setSaving(true);
    try {
        const orgId = profile.organization_id;
        const monto = Number(form.monto);

        if (isEditing && form.id) {
            // --- MODO ACTUALIZACIÓN ---
            // Solo actualizamos la tabla de gastos, no tocamos la caja/banco para evitar desfasajes complejos
            // Si el usuario quiere corregir el dinero, debería anular y crear de nuevo.
            // Aquí permitimos corregir errores de tipeo, categoría o fecha.
            const { error: updateError } = await supabase
                .from('gastos')
                .update({
                    concepto: form.concepto.toUpperCase(),
                    monto: monto,
                    fecha: form.fecha,
                    sitio_id: form.sitio_id || null,
                    observaciones: form.observaciones,
                    categoria: form.categoria
                })
                .eq('id', form.id);
            
            if (updateError) throw updateError;
            toast.success("Gasto actualizado");

        } else {
            // --- MODO CREACIÓN ---
            const esCtaCte = form.metodo_pago === METODO_CTA_CTE;
            const { data: gastoCreado, error: gastoError } = await supabase.from('gastos').insert({
                organization_id: orgId,
                concepto: form.concepto.toUpperCase(),
                monto: monto,
                fecha: form.fecha,
                sitio_id: form.sitio_id || null,
                observaciones: form.observaciones,
                is_active: true,
                categoria: form.categoria,
                condicion_pago: esCtaCte ? 'cuenta_corriente' : 'contado',
                proveedor_id: esCtaCte ? form.proveedor_id : null
            }).select('id').single();

            if (gastoError) throw gastoError;

            if (esCtaCte) {
                // Deuda con el proveedor: suma saldo y queda en su cuenta corriente.
                // No hay movimiento de fondos hasta que se pague desde Tesorería.
                const { data: prov, error: provErr } = await supabase
                    .from('proveedores').select('saldo').eq('id', form.proveedor_id).single();
                if (provErr) throw provErr;
                const nuevoSaldo = (Number(prov?.saldo) || 0) + monto;
                const { error: updProvErr } = await supabase
                    .from('proveedores').update({ saldo: nuevoSaldo }).eq('id', form.proveedor_id);
                if (updProvErr) throw updProvErr;
                const { error: movProvErr } = await supabase.from('proveedor_movimientos').insert([{
                    organization_id: orgId,
                    proveedor_id: form.proveedor_id,
                    gasto_id: gastoCreado.id,
                    tipo: 'Gasto',
                    monto: monto,
                    saldo_resultante: nuevoSaldo,
                    fecha: buildLocalOperationTimestamp(form.fecha),
                    observaciones: `Gasto: ${form.concepto.toUpperCase()}`
                }]);
                if (movProvErr) throw movProvErr;
            }

            // Impacto Financiero (Solo al crear)
            if (form.metodo_pago === 'Transferencia') {
                const { data: bank } = await supabase.from('banks').select('current_balance').eq('id', form.banco_id).single();
                const newBalance = (Number(bank?.current_balance) || 0) - monto;
                
                await supabase.from('banks').update({ current_balance: newBalance }).eq('id', form.banco_id);
                
                await supabase.from('movimientos_financieros').insert({
                    organization_id: orgId,
                    bank_id: form.banco_id,
                    sitio_id: form.sitio_id || null,
                    tipo: 'Egreso',
                    categoria: form.categoria === 'Fijo' ? 'Gasto Fijo' : 'Gasto Variable', 
                    monto: monto,
                    descripcion: `Gasto: ${form.concepto}`,
                    fecha_operacion: new Date(),
                    saldo_resultante: newBalance,
                    user_id: profile.id,
                    documento_tipo: 'Gasto',
                    documento_id: gastoCreado.id
                });
            } 
            // ✅ Regla de arqueo: los gastos cargados desde Tesorería/Gastos NO entran
            // a la caja. El arqueo de caja solo cuenta los movimientos cargados desde
            // su propio popout (Caja.jsx → registrarMovimiento). Por eso un gasto en
            // efectivo desde acá queda registrado en `gastos` (estado de resultados)
            // pero no inserta en `movimientos_caja`.
            toast.success(esCtaCte ? "Gasto imputado a la cuenta corriente del proveedor" : "Gasto registrado correctamente");
        }

        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        setShowModal(false);
        loadGastos();

    } catch (err) {
        console.error(err);
        toast.error("Error al guardar");
    } finally {
        setSaving(false);
    }
  };

  const handleAnular = async (id) => {
      setProcessingDeleteId(id);
      try {
          const { data: gastoRow } = await supabase
            .from('gastos')
            .select('id, monto, concepto, condicion_pago, proveedor_id, is_active')
            .eq('id', id)
            .maybeSingle();
          if (gastoRow && gastoRow.is_active === false) {
            toast('El gasto ya estaba anulado');
            setConfirmDeleteId(null);
            return;
          }

          // 1. Marcar el gasto como inactivo
          const { error } = await supabase
            .from('gastos')
            .update({ is_active: false })
            .eq('id', id);

          if (error) throw error;

          // 1.b Gasto en cuenta corriente: revertir la deuda con el proveedor.
          if (isGastoCtaCte(gastoRow) && gastoRow.proveedor_id) {
              const montoGasto = Number(gastoRow.monto) || 0;
              const { data: prov } = await supabase.from('proveedores').select('saldo').eq('id', gastoRow.proveedor_id).single();
              const saldoRevertido = (Number(prov?.saldo) || 0) - montoGasto;
              await supabase.from('proveedores').update({ saldo: saldoRevertido }).eq('id', gastoRow.proveedor_id);
              await supabase.from('proveedor_movimientos').insert([{
                  organization_id: profile.organization_id,
                  proveedor_id: gastoRow.proveedor_id,
                  gasto_id: gastoRow.id,
                  tipo: 'Anulacion de Gasto',
                  monto: -montoGasto,
                  saldo_resultante: saldoRevertido,
                  fecha: buildLocalOperationTimestamp(),
                  observaciones: `(ANULADO) Gasto: ${gastoRow.concepto || ''}`
              }]);
          }

          // 2. Buscar si hay movimiento financiero asociado para revertir saldo
          const { data: movFinanciero } = await supabase
            .from('movimientos_financieros')
            .select('*')
            .eq('documento_id', id)
            .eq('documento_tipo', 'Gasto')
            .maybeSingle();

          if (movFinanciero && movFinanciero.bank_id) {
              // Revertir saldo del banco
              const { data: bankData } = await supabase.from('banks').select('current_balance').eq('id', movFinanciero.bank_id).single();
              if (bankData) {
                  const restoredBankBalance = Number(bankData.current_balance) + Number(movFinanciero.monto);
                  await supabase.from('banks').update({ current_balance: restoredBankBalance }).eq('id', movFinanciero.bank_id);
              }
              // Soft delete del movimiento financiero
              await supabase.from('movimientos_financieros')
                .update({ 
                    is_active: false, 
                    monto: 0,
                    descripcion: `(ANULADO) Gasto` 
                })
                .eq('id', movFinanciero.id);
          } else {
              // 3. Si no hay financiero, buscar si fue por caja
              const { data: movCaja } = await supabase
                .from('movimientos_caja')
                .select('id')
                .eq('documento_id', id)
                .maybeSingle();
                
              if (movCaja) {
                  // Borramos el movimiento de caja para que vuelva el saldo
                  await supabase.from('movimientos_caja').delete().eq('id', movCaja.id);
              }
          }

          toast.success("Gasto y fondos anulados correctamente");
          setConfirmDeleteId(null);
          loadGastos();
      } catch (e) {
          console.error(e);
          toast.error("Error al anular");
      } finally {
          setProcessingDeleteId(null);
      }
  };

  const filteredGastos = useMemo(() => {
      return gastos.filter(g => {
          const matchText = (g.concepto || '').toLowerCase().includes(q.toLowerCase());
          const matchSitio = filterSitio === 'all' || g.sitio_id === filterSitio;
          const matchCat = filterCategoria === 'all' || (g.categoria || 'Variable') === filterCategoria;
          return matchText && matchSitio && matchCat;
      });
  }, [gastos, q, filterSitio, filterCategoria]);

  const totalPeriodo = filteredGastos.reduce((acc, g) => acc + (g.is_active ? Number(g.monto) : 0), 0);
  const totalFijo = filteredGastos.reduce((acc, g) => acc + (g.is_active && g.categoria === 'Fijo' ? Number(g.monto) : 0), 0);
  const totalVariable = filteredGastos.reduce((acc, g) => acc + (g.is_active && (g.categoria === 'Variable' || !g.categoria) ? Number(g.monto) : 0), 0);

  return (
    <div className="w-full p-4 md:p-6 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <TrendingDown size={18} className="text-[#F05A57]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Gastos</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Control de Egresos Operativos</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-slate-200 shadow-sm">
                <Calendar size={14} className="text-slate-400"/>
                <input 
                    type="date" 
                    value={fechaDesde} 
                    onChange={e => setFechaDesde(e.target.value)}
                    className="text-[10px] font-bold uppercase bg-transparent outline-none text-slate-600 w-24"
                />
                <span className="text-slate-300">-</span>
                <input 
                    type="date" 
                    value={fechaHasta} 
                    onChange={e => setFechaHasta(e.target.value)}
                    className="text-[10px] font-bold uppercase bg-transparent outline-none text-slate-600 w-24"
                />
            </div>
            
            <button
              onClick={handleCreateClick}
              className="px-5 py-2.5 rounded-2xl bg-[#F05A57] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            >
              <Plus size={16} strokeWidth={3} /> Nuevo Gasto
            </button>
        </div>
      </div>

      {/* KPI TOTALES */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between relative overflow-hidden group">
            <div className="relative z-10">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Total Gastos Periodo</p>
                <h2 className="text-2xl font-black text-slate-800 tracking-tight">$ {totalPeriodo.toLocaleString('es-AR')}</h2>
            </div>
            <div className="h-10 w-10 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 relative z-10">
                <Activity size={20} />
            </div>
            <div className="absolute right-0 bottom-0 opacity-5 group-hover:opacity-10 transition-opacity">
                <Activity size={60} />
            </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between relative overflow-hidden group">
            <div className="relative z-10">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Gastos Fijos</p>
                <h2 className="text-xl font-black text-slate-700 tracking-tight">$ {totalFijo.toLocaleString('es-AR')}</h2>
            </div>
            <div className="h-10 w-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-500 relative z-10">
                <ArrowDownCircle size={20} />
            </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between relative overflow-hidden group">
            <div className="relative z-10">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Gastos Variables</p>
                <h2 className="text-xl font-black text-slate-700 tracking-tight">$ {totalVariable.toLocaleString('es-AR')}</h2>
            </div>
            <div className="h-10 w-10 bg-amber-50 rounded-full flex items-center justify-center text-amber-500 relative z-10">
                <PieChart size={20} />
            </div>
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-4">
        <div className="flex flex-col md:flex-row gap-3 items-center">
            <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input 
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="BUSCAR CONCEPTO..."
                    className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold outline-none focus:border-[#F05A57] uppercase text-slate-700 transition-all focus:ring-2 focus:ring-[#F05A57]/10"
                />
            </div>
            
            <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                <div className="relative">
                    <MapPin size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <select
                        value={filterSitio}
                        onChange={e => setFilterSitio(e.target.value)}
                        className="pl-8 pr-8 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#F05A57] cursor-pointer text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        <option value="all">Todas las Sucursales</option>
                        {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                    </select>
                </div>

                <div className="relative">
                    <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <select
                        value={filterCategoria}
                        onChange={e => setFilterCategoria(e.target.value)}
                        className="pl-8 pr-8 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#F05A57] cursor-pointer text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        <option value="all">Todas las Categorías</option>
                        <option value="Fijo">Gastos Fijos</option>
                        <option value="Variable">Gastos Variables</option>
                    </select>
                </div>

                <button 
                    onClick={() => setMostrarEliminados(!mostrarEliminados)}
                    className={`p-2.5 rounded-xl border transition-all ${mostrarEliminados ? 'bg-rose-50 border-rose-200 text-rose-600' : 'bg-white border-slate-100 text-slate-400 hover:text-slate-600'}`}
                    title="Ver Eliminados"
                >
                    {mostrarEliminados ? <Eye size={16}/> : <EyeOff size={16}/>}
                </button>
            </div>
        </div>
      </div>

      {/* MODAL NUEVO / EDITAR GASTO */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B2A36]/80 backdrop-blur-sm p-4">
            <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl animate-nova-in overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="bg-[#0B2A36] p-6 text-white flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-black uppercase tracking-tight">
                            {isEditing ? 'Editar Gasto' : 'Registrar Gasto'}
                        </h2>
                        <p className="text-[10px] font-bold opacity-70 uppercase tracking-widest">
                            {isEditing ? 'Modificar datos del registro' : 'Salida de dinero operativa'}
                        </p>
                    </div>
                    <button onClick={() => setShowModal(false)} className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-all"><X size={18}/></button>
                </div>
                
                <form onSubmit={handleSave} className="p-6 md:p-8 space-y-4">
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Concepto</label>
                            <input 
                                autoFocus
                                value={form.concepto}
                                onChange={e => setForm({...form, concepto: e.target.value})}
                                className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-[#F05A57] uppercase mt-1"
                                placeholder="Ej: Compra Artículos Limpieza"
                            />
                        </div>
                        <div>
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Categoría</label>
                            <select
                                value={form.categoria}
                                onChange={e => setForm({...form, categoria: e.target.value})}
                                className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-[#F05A57] uppercase mt-1 cursor-pointer"
                            >
                                <option value="Variable">Variable</option>
                                <option value="Fijo">Fijo</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Monto</label>
                            <div className="relative mt-1">
                                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                                <input 
                                    type="number"
                                    value={form.monto}
                                    onChange={e => setForm({...form, monto: e.target.value})}
                                    className="w-full pl-8 pr-3 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm font-black outline-none focus:border-[#F05A57]"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha</label>
                            <input 
                                type="date"
                                value={form.fecha}
                                onChange={e => setForm({...form, fecha: e.target.value})}
                                className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-[#F05A57] mt-1"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Sucursal</label>
                            <select
                                value={form.sitio_id}
                                onChange={e => setForm({...form, sitio_id: e.target.value})}
                                className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-[#F05A57] mt-1 uppercase cursor-pointer"
                            >
                                <option value="">-- General / Todas --</option>
                                {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                            </select>
                        </div>
                        
                        {/* Si estamos editando, ocultamos el metodo de pago porque no se puede cambiar facilmente sin romper consistencia */}
                        {!isEditing && (
                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Método Pago</label>
                                <select
                                    value={form.metodo_pago}
                                    onChange={e => setForm({...form, metodo_pago: e.target.value})}
                                    className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-[#F05A57] mt-1 uppercase cursor-pointer"
                                >
                                    <option value="Efectivo">Efectivo (Caja)</option>
                                    <option value="Transferencia">Transferencia (Banco)</option>
                                    <option value={METODO_CTA_CTE}>Cuenta Corriente (Proveedor)</option>
                                </select>
                            </div>
                        )}
                        {isEditing && (
                             <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Método Pago</label>
                                <div className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-400 mt-1 uppercase truncate">
                                    {form.condicion_pago === 'cuenta_corriente'
                                        ? `Cta. Cte. ${proveedores.find(p => p.id === form.proveedor_id)?.razon_social || ''}`
                                        : 'No modificable'}
                                </div>
                            </div>
                        )}
                    </div>

                    {!isEditing && form.metodo_pago === METODO_CTA_CTE && (
                        <div className="animate-in fade-in slide-in-from-top-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Proveedor</label>
                            <select
                                value={form.proveedor_id}
                                onChange={e => setForm({...form, proveedor_id: e.target.value})}
                                className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-[#F05A57] mt-1 uppercase cursor-pointer"
                            >
                                <option value="">Seleccione Proveedor...</option>
                                {proveedores.map(p => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                            </select>
                            <p className="text-[9px] font-bold text-slate-400 mt-1 ml-1">
                                No mueve caja ni banco: suma deuda al proveedor. Se cancela desde Tesorería &gt; Pagos.
                            </p>
                        </div>
                    )}

                    {!isEditing && form.metodo_pago === 'Transferencia' && (
                        <div className="animate-in fade-in slide-in-from-top-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Cuenta Bancaria Origen</label>
                            <select
                                value={form.banco_id}
                                onChange={e => setForm({...form, banco_id: e.target.value})}
                                className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-[#F05A57] mt-1 uppercase cursor-pointer"
                            >
                                <option value="">Seleccione Banco...</option>
                                {bancos.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Observaciones</label>
                        <textarea 
                            value={form.observaciones}
                            onChange={e => setForm({...form, observaciones: e.target.value})}
                            className="w-full p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold outline-none focus:border-[#F05A57] uppercase mt-1 resize-none h-20"
                            placeholder="Detalle opcional..."
                        />
                    </div>

                    <button 
                        type="submit" 
                        disabled={saving}
                        className={`w-full py-4 rounded-xl font-black text-[10px] uppercase tracking-[0.3em] flex items-center justify-center gap-2 transition-all shadow-lg ${
                            isSuccess ? 'bg-emerald-500 text-white' : 'bg-[#F05A57] text-white hover:brightness-110'
                        }`}
                    >
                        {saving ? <RefreshCcw size={16} className="animate-spin"/> : isSuccess ? <Check size={18}/> : <Save size={16}/>}
                        {isSuccess ? 'Guardado' : (isEditing ? 'Actualizar Gasto' : 'Confirmar Gasto')}
                    </button>
                </form>
            </div>
        </div>
      )}

      {/* LISTADO */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-10">
        <div className="p-0">
          <div className="overflow-x-auto imp-table-wrap">
            <div className="min-w-[800px]">
                {/* HEADER TABLA */}
                <div className="bg-slate-50/80 border-b border-slate-100 px-6 py-3 grid grid-cols-12 gap-4 text-[9px] font-black text-slate-400 uppercase tracking-widest items-center">
                    <div className="col-span-2">Fecha</div>
                    <div className="col-span-3">Concepto</div>
                    <div className="col-span-2 text-center">Categoría</div>
                    <div className="col-span-2">Sucursal</div>
                    <div className="col-span-2 text-right">Monto</div>
                    <div className="col-span-1 text-right"></div>
                </div>

                {/* BODY TABLA */}
                <div className="divide-y divide-slate-50">
                    {loading ? (
                        <div className="py-20 flex justify-center text-slate-300"><RefreshCcw className="animate-spin"/></div>
                    ) : filteredGastos.length === 0 ? (
                        <div className="py-20 text-center text-slate-300 text-[10px] font-black uppercase tracking-widest">No hay gastos registrados en este período</div>
                    ) : (
                        filteredGastos.map(g => (
                        <React.Fragment key={g.id}>
                            <div 
                                className={`group px-6 py-4 grid grid-cols-12 gap-4 items-center transition-all cursor-pointer ${
                                    !g.is_active ? 'bg-rose-50/30 opacity-60' : 'hover:bg-slate-50'
                                }`}
                                // CAMBIO: Click ahora abre el modal de edición
                                onClick={() => handleEditClick(g)}
                            >
                                <div className="col-span-2 text-[10px] font-bold text-slate-500 flex items-center gap-2">
                                    <Calendar size={12}/> {new Date(g.fecha).toLocaleDateString('es-AR', {timeZone: 'UTC'})}
                                </div>
                                <div className="col-span-3">
                                    <p className={`text-[11px] font-black uppercase truncate ${!g.is_active ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                                        {g.concepto}
                                    </p>
                                    {g.origen === 'automatico' && (
                                        <span className="text-[8px] font-bold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded ml-1 uppercase tracking-wider flex items-center gap-1 w-fit mt-1">
                                            <Truck size={8}/> Pedido Web
                                        </span>
                                    )}
                                    {isGastoCtaCte(g) && (
                                        <span className="text-[8px] font-bold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded uppercase tracking-wider flex items-center gap-1 w-fit mt-1 max-w-full truncate">
                                            <FileText size={8}/> Cta. Cte. {g.proveedores?.razon_social || 'Proveedor'}
                                        </span>
                                    )}
                                </div>
                                
                                <div className="col-span-2 text-center">
                                    <span className={`text-[9px] font-bold uppercase px-2 py-1 rounded-lg border ${
                                        (g.categoria === 'Fijo') 
                                            ? 'bg-blue-50 text-blue-600 border-blue-100' 
                                            : 'bg-amber-50 text-amber-600 border-amber-100'
                                    }`}>
                                        {g.categoria || 'Variable'}
                                    </span>
                                </div>

                                <div className="col-span-2">
                                    <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-500 px-2 py-1 rounded-lg">
                                        {g.sitios?.nombre || 'Consolidado'}
                                    </span>
                                </div>
                                <div className="col-span-2 text-right">
                                    <span className="text-sm font-black text-rose-500 tracking-tight">- $ {Number(g.monto).toLocaleString('es-AR')}</span>
                                </div>
                                <div className="col-span-1 flex justify-end gap-2 relative" onClick={e => e.stopPropagation()}>
                                    {g.origen !== 'automatico' && g.is_active && (
                                        confirmDeleteId === g.id ? (
                                            // NUEVO: Mensaje de confirmación con texto
                                            <div className="flex flex-col gap-1 bg-white shadow-xl p-2 rounded-xl absolute right-0 top-full z-50 border border-slate-100 animate-in zoom-in-95 origin-top-right w-24">
                                                <button 
                                                    onClick={() => handleAnular(g.id)} 
                                                    className="w-full bg-rose-500 text-white text-[9px] font-black uppercase py-1.5 rounded hover:bg-rose-600"
                                                    disabled={processingDeleteId === g.id}
                                                >
                                                    {processingDeleteId === g.id ? '...' : 'Si, borrar'}
                                                </button>
                                                <button onClick={() => setConfirmDeleteId(null)} className="w-full bg-slate-100 text-slate-500 text-[9px] font-black uppercase py-1.5 rounded hover:bg-slate-200">
                                                    No
                                                </button>
                                            </div>
                                        ) : (
                                            <button 
                                                onClick={() => setConfirmDeleteId(g.id)}
                                                className="p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                                                title="Anular Gasto"
                                            >
                                                <Trash2 size={16}/>
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>
                            
                            {/* COMENTADO: Vista detallada anterior
                            {expandedId === g.id && (
                            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 grid grid-cols-12 animate-in fade-in slide-in-from-top-2">
                                <div className="col-span-12 md:col-span-10 md:col-start-2 bg-white border border-slate-200 p-4 rounded-xl shadow-sm relative overflow-hidden">
                                    <div className="absolute top-0 left-0 w-1 h-full bg-[#F05A57]"></div>
                                    <div className="flex items-center gap-2 text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">
                                    <FileText size={12}/> Detalle { !g.is_active && <span className="text-rose-500 ml-2">(GASTO ANULADO)</span> }
                                    </div>
                                    <p className="text-[10px] font-bold text-slate-700 uppercase leading-relaxed pr-24">
                                    {g.observaciones || "Sin observaciones adicionales."}
                                    </p>
                                </div>
                            </div>
                            )}
                            */}
                        </React.Fragment>
                        ))
                    )}
                </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
}

export default Gastos;
